import React from 'react';
import { Box, Stack, Typography, TextField, Divider, Select, MenuItem, Checkbox, FormControlLabel, Button, IconButton, Alert } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import type { IssueCastUpdates } from 'api/apiClient';

export interface IssueCastEditSectionProps {
  value?: IssueCastUpdates;
  onChange: (v: IssueCastUpdates) => void;
  websiteId: string;
  onCancel: () => void;
  onUpdate: () => void | Promise<void>;
  disabled?: boolean;
}

const IssueCastEditSection: React.FC<IssueCastEditSectionProps> = ({ value, onChange, websiteId, onCancel, onUpdate, disabled }) => {
  const v = value || {};

  // Helper functions for date handling
  const isoToDatetimeLocal = (isoString: string): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      // Convert to local datetime-local format (YYYY-MM-DDTHH:mm)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  const datetimeLocalToISO = (datetimeLocal: string): string => {
    if (!datetimeLocal) return '';
    try {
      const date = new Date(datetimeLocal);
      return date.toISOString();
    } catch {
      return datetimeLocal;
    }
  };

  const formatReadableDate = (isoString: string): string => {
    if (!isoString) return 'Not set';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const setPublishDateToNow = () => {
    onChange({ ...v, publishDate: new Date().toISOString() });
  };

  const sectionLabelStyle = { fontSize: '1.35rem' };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          Important: Changes are saved immediately
        </Typography>
        <Typography variant="body2">
          Edits to this podcast's metadata will affect the live podcast when you click "Update". The audio content itself is locked and cannot be changed once published.
        </Typography>
      </Alert>

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Core</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" size="small" fullWidth value={v.name ?? ''} onChange={(e) => onChange({ ...v, name: e.target.value })} disabled={disabled} />
          <TextField label="Description" size="small" fullWidth multiline rows={2} value={v.description ?? ''} onChange={(e) => onChange({ ...v, description: e.target.value })} disabled={disabled} />
          <TextField label="HLS URL" size="small" fullWidth value={v.hlsUrl ?? ''} onChange={(e) => onChange({ ...v, hlsUrl: e.target.value })} disabled={disabled} />
          
          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Publish Date"
                size="small"
                fullWidth
                type="datetime-local"
                value={isoToDatetimeLocal(v.publishDate ?? '')}
                onChange={(e) => onChange({ ...v, publishDate: datetimeLocalToISO(e.target.value) })}
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
                helperText={v.publishDate ? formatReadableDate(v.publishDate) : 'Select a publish date and time'}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AccessTimeIcon />}
                onClick={setPublishDateToNow}
                disabled={disabled}
                sx={{ mt: 0.5, minWidth: 'fit-content', whiteSpace: 'nowrap' }}
              >
                Now
              </Button>
            </Stack>
            <TextField
              label="Publish Date (ISO Format)"
              size="small"
              fullWidth
              value={v.publishDate ?? ''}
              onChange={(e) => onChange({ ...v, publishDate: e.target.value })}
              disabled={disabled}
              placeholder="2025-10-01T09:00:00Z"
              helperText="Advanced: Edit ISO 8601 format directly"
              sx={{ mt: 2 }}
            />
          </Box>

          <TextField label="Website ID" size="small" fullWidth value={websiteId} disabled />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Podcast Type</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Select
            size="small"
            fullWidth
            value={v.podcastType ?? 'multiple_articles'}
            onChange={(e) => onChange({ ...v, podcastType: e.target.value as any })}
            disabled={disabled}
          >
            <MenuItem value="multiple_articles">Multiple Articles</MenuItem>
            <MenuItem value="emergency">Emergency</MenuItem>
          </Select>

          {v.podcastType === 'emergency' && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider', mt: 2 }}>
              <Typography variant="subtitle2">Emergency Classification</Typography>

              {/* Locations */}
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">Locations</Typography>
                {(v.classification?.locations || []).map((item, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      value={item}
                      onChange={(e) => {
                        const newItems = [...(v.classification?.locations || [])];
                        newItems[idx] = e.target.value;
                        onChange({ ...v, classification: { ...(v.classification || {}), locations: newItems } });
                      }}
                      disabled={disabled}
                    />
                    <IconButton size="small" color="error" onClick={() => {
                      const newItems = (v.classification?.locations || []).filter((_, i) => i !== idx);
                      onChange({ ...v, classification: { ...(v.classification || {}), locations: newItems } });
                    }} disabled={disabled}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => {
                    const newItems = [...(v.classification?.locations || []), ''];
                    onChange({ ...v, classification: { ...(v.classification || {}), locations: newItems } });
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                  disabled={disabled}
                >
                  Add Location
                </Button>
              </Stack>

              {/* Severity Tags */}
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">Severity Tags</Typography>
                {(v.classification?.severity_tags || []).map((item, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      value={item}
                      onChange={(e) => {
                        const newItems = [...(v.classification?.severity_tags || [])];
                        newItems[idx] = e.target.value;
                        onChange({ ...v, classification: { ...(v.classification || {}), severity_tags: newItems } });
                      }}
                      disabled={disabled}
                    />
                    <IconButton size="small" color="error" onClick={() => {
                      const newItems = (v.classification?.severity_tags || []).filter((_, i) => i !== idx);
                      onChange({ ...v, classification: { ...(v.classification || {}), severity_tags: newItems } });
                    }} disabled={disabled}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => {
                    const newItems = [...(v.classification?.severity_tags || []), ''];
                    onChange({ ...v, classification: { ...(v.classification || {}), severity_tags: newItems } });
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                  disabled={disabled}
                >
                  Add Severity Tag
                </Button>
              </Stack>

              {/* Incident Types */}
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">Incident Types</Typography>
                {(v.classification?.incident_types || []).map((item, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      value={item}
                      onChange={(e) => {
                        const newItems = [...(v.classification?.incident_types || [])];
                        newItems[idx] = e.target.value;
                        onChange({ ...v, classification: { ...(v.classification || {}), incident_types: newItems } });
                      }}
                      disabled={disabled}
                    />
                    <IconButton size="small" color="error" onClick={() => {
                      const newItems = (v.classification?.incident_types || []).filter((_, i) => i !== idx);
                      onChange({ ...v, classification: { ...(v.classification || {}), incident_types: newItems } });
                    }} disabled={disabled}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => {
                    const newItems = [...(v.classification?.incident_types || []), ''];
                    onChange({ ...v, classification: { ...(v.classification || {}), incident_types: newItems } });
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                  disabled={disabled}
                >
                  Add Incident Type
                </Button>
              </Stack>

              <TextField
                label="Timestamp"
                size="small"
                fullWidth
                type="datetime-local"
                value={isoToDatetimeLocal(v.classification?.timestamp ?? '')}
                onChange={(e) => onChange({ 
                  ...v, 
                  classification: { 
                    ...(v.classification || {}), 
                    timestamp: datetimeLocalToISO(e.target.value) 
                  } 
                })}
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Website Branding</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" size="small" fullWidth value={v.websiteBranding?.name ?? ''} onChange={(e) => onChange({ ...v, websiteBranding: { ...(v.websiteBranding || {}), name: e.target.value } })} disabled={disabled} />
          <TextField label="Tagline" size="small" fullWidth value={v.websiteBranding?.tagline ?? ''} onChange={(e) => onChange({ ...v, websiteBranding: { ...(v.websiteBranding || {}), tagline: e.target.value } })} disabled={disabled} />
          <TextField label="Location Name" size="small" fullWidth value={v.websiteBranding?.locationName ?? ''} onChange={(e) => onChange({ ...v, websiteBranding: { ...(v.websiteBranding || {}), locationName: e.target.value } })} disabled={disabled} />
          <TextField label="Website URL" size="small" fullWidth value={v.websiteBranding?.websiteUrl ?? ''} onChange={(e) => onChange({ ...v, websiteBranding: { ...(v.websiteBranding || {}), websiteUrl: e.target.value } })} disabled={disabled} />
          <TextField label="Logo URL" size="small" fullWidth value={v.websiteBranding?.logoUrl ?? ''} onChange={(e) => onChange({ ...v, websiteBranding: { ...(v.websiteBranding || {}), logoUrl: e.target.value } })} disabled={disabled} />
          <TextField label="Background Gradient" size="small" fullWidth value={v.websiteBranding?.backgroundGradient ?? ''} onChange={(e) => onChange({ ...v, websiteBranding: { ...(v.websiteBranding || {}), backgroundGradient: e.target.value } })} disabled={disabled} />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Advertisement</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" size="small" fullWidth value={v.advertisement?.name ?? ''} onChange={(e) => onChange({ ...v, advertisement: { ...(v.advertisement || {}), name: e.target.value } })} disabled={disabled} />
          <TextField label="Sponsor Mention" size="small" fullWidth value={v.advertisement?.sponsorMention ?? ''} onChange={(e) => onChange({ ...v, advertisement: { ...(v.advertisement || {}), sponsorMention: e.target.value } })} disabled={disabled} />
          <TextField label="Logo URL" size="small" fullWidth value={v.advertisement?.logoURL ?? ''} onChange={(e) => onChange({ ...v, advertisement: { ...(v.advertisement || {}), logoURL: e.target.value } })} disabled={disabled} />
          <TextField label="Click-through URL" size="small" fullWidth value={v.advertisement?.clickThroughURL ?? ''} onChange={(e) => onChange({ ...v, advertisement: { ...(v.advertisement || {}), clickThroughURL: e.target.value } })} disabled={disabled} />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Weather</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControlLabel control={<Checkbox checked={!!v.weather?.enabled} onChange={(e) => onChange({ ...v, weather: { ...(v.weather || {}), enabled: e.target.checked } })} />} label="Enabled" disabled={disabled} />
          <TextField label="Location Name" size="small" fullWidth value={v.weather?.locationName ?? ''} onChange={(e) => onChange({ ...v, weather: { ...(v.weather || {}), locationName: e.target.value } })} disabled={disabled} />
          <TextField label="Latitude" size="small" fullWidth value={(v.weather?.latitude ?? '') as any} onChange={(e) => onChange({ ...v, weather: { ...(v.weather || {}), latitude: e.target.value } })} disabled={disabled} />
          <TextField label="Longitude" size="small" fullWidth value={(v.weather?.longitude ?? '') as any} onChange={(e) => onChange({ ...v, weather: { ...(v.weather || {}), longitude: e.target.value } })} disabled={disabled} />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Events</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControlLabel control={<Checkbox checked={!!v.events?.enabled} onChange={(e) => onChange({ ...v, events: { ...(v.events || {}), enabled: e.target.checked } })} />} label="Enabled" disabled={disabled} />
          <TextField label="Interests" size="small" fullWidth value={v.events?.interests ?? ''} onChange={(e) => onChange({ ...v, events: { ...(v.events || {}), interests: e.target.value } })} disabled={disabled} />
          <FormControlLabel control={<Checkbox checked={!!v.events?.usePublisherEvents} onChange={(e) => onChange({ ...v, events: { ...(v.events || {}), usePublisherEvents: e.target.checked } })} disabled={!v.events?.enabled || disabled} />} label="Use Publisher Events" />
          <FormControlLabel control={<Checkbox checked={!!v.events?.useSearchEvents} onChange={(e) => onChange({ ...v, events: { ...(v.events || {}), useSearchEvents: e.target.checked } })} disabled={!v.events?.enabled || disabled} />} label="Use Search Events" />
          <Typography variant="caption" color="text.secondary">
            Use Publisher Events pulls events curated or imported for this publisher. Use Search Events pulls events from external search sources for the website's locale. Disable both to rely only on manually selected interests.
          </Typography>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Conversational AI</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControlLabel control={<Checkbox checked={!!v.convai?.enabled} onChange={(e) => onChange({ ...v, convai: { ...(v.convai || {}), enabled: e.target.checked } })} />} label="Enabled" disabled={disabled} />
          <TextField label="Voice ID" size="small" fullWidth value={v.convai?.voiceId ?? ''} onChange={(e) => onChange({ ...v, convai: { ...(v.convai || {}), voiceId: e.target.value } })} disabled={disabled} helperText="The ID of the voice to use for the conversational agent" />
          <TextField label="Agent ID" size="small" fullWidth value={v.convai?.agentId ?? ''} onChange={(e) => onChange({ ...v, convai: { ...(v.convai || {}), agentId: e.target.value } })} disabled={disabled} helperText="The ID of the agent to use for the conversational agent" />
          <TextField label="First Message" size="small" fullWidth multiline rows={2} value={v.convai?.firstMessage ?? ''} onChange={(e) => onChange({ ...v, convai: { ...(v.convai || {}), firstMessage: e.target.value } })} disabled={disabled} helperText="The initial message the agent will say when a user connects" />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Favicons</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(v.favicons || []).map((url, idx) => (
            <Stack key={idx} direction="row" spacing={1} alignItems="center">
              <Box component="img" src={url} alt={`Favicon ${idx + 1}`} sx={{ width: 32, height: 32, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 0.5, bgcolor: 'background.paper' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              <TextField label={`Favicon URL ${idx + 1}`} size="small" fullWidth value={url} onChange={(e) => {
                const newFavicons = [...(v.favicons || [])];
                newFavicons[idx] = e.target.value;
                onChange({ ...v, favicons: newFavicons });
              }} disabled={disabled} />
              <Button size="small" color="error" onClick={() => { const newFavicons = (v.favicons || []).filter((_, i) => i !== idx); onChange({ ...v, favicons: newFavicons }); }}>
                <DeleteOutlineIcon />
              </Button>
            </Stack>
          ))}
          <Button variant="outlined" size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => { const newFavicons = [...(v.favicons || []), '']; onChange({ ...v, favicons: newFavicons }); }} sx={{ alignSelf: 'flex-start' }} disabled={disabled}>
            Add Favicon URL
          </Button>
          <Typography variant="caption" color="text.secondary">
            Add URLs to favicon images. Standard sizes include 16x16, 32x32, and apple-touch-icon (180x180).
          </Typography>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="overline" sx={sectionLabelStyle}>Podcasts</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(v.podcasts || []).map((podcast, idx) => (
            <Stack key={idx} direction="row" spacing={1} alignItems="center">
              <Select size="small" value={podcast.type as any} onChange={(e) => { const newPodcasts = [...(v.podcasts || [])]; newPodcasts[idx] = { ...newPodcasts[idx], type: e.target.value as any }; onChange({ ...v, podcasts: newPodcasts }); }} sx={{ minWidth: 150 }} disabled={disabled}>
                <MenuItem value="Spotify">Spotify</MenuItem>
                <MenuItem value="Apple Podcasts">Apple Podcasts</MenuItem>
              </Select>
              <TextField label="Podcast URL" size="small" fullWidth value={podcast.url} onChange={(e) => { const newPodcasts = [...(v.podcasts || [])]; newPodcasts[idx] = { ...newPodcasts[idx], url: e.target.value }; onChange({ ...v, podcasts: newPodcasts }); }} placeholder="https://..." disabled={disabled} />
              <IconButton size="small" color="error" onClick={() => { const newPodcasts = (v.podcasts || []).filter((_, i) => i !== idx); onChange({ ...v, podcasts: newPodcasts }); }} disabled={disabled}>
                <DeleteOutlineIcon />
              </IconButton>
            </Stack>
          ))}
          <Button variant="outlined" size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => { const newPodcasts = [...(v.podcasts || []), { type: 'Spotify', url: '' } as any]; onChange({ ...v, podcasts: newPodcasts }); }} sx={{ alignSelf: 'flex-start' }} disabled={disabled}>
            Add Podcast
          </Button>
          <Typography variant="caption" color="text.secondary">
            Add links to podcast platforms where your podcast is available.
          </Typography>
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="outlined" onClick={onCancel} disabled={disabled}>Cancel</Button>
        <Button variant="contained" onClick={onUpdate} disabled={disabled}>Update</Button>
      </Stack>
    </Stack>
  );
};

export default IssueCastEditSection;
